<template>	
	<div class="sideways-content-fold" v-if="activePanel">
		<div class="portlet light bordered" v-if="process === 'form'">
			<div class="portlet-title">
				<div class="caption">
					Invite A User
				</div>
			</div>
			<div class="portlet-body">
				<form role="form" @submit.prevent="inviteUser($event)" novalidate>
					<div class="alert alert-danger" v-if="errorMessage.length">
					    <button class="close" data-close="alert" @click="clearError()"></button>
                        <span>{{ errorMessage }}</span>
					</div>
					<div class="form-body invite-user-form">
						<div class="form-group form-md-line-input form-md-floating-label">
                            <input type="email" class="form-control input-sm" :class="{'edited': invitedUser.email.length}" id="invite_user_email" v-model="invitedUser.email">
                            <label for="invite_user_email">Email</label>
                            <span class="help-block">Please make sure you enter a valid email address.</span>
                        </div>
                        <div class="form-group form-md-line-input form-md-floating-label">
                            <input type="text" class="form-control input-sm" :class="{'edited': invitedUser.name.first_name && invitedUser.name.first_name.length}" id="invite_user_first_name" v-model="invitedUser.name.first_name">
                            <label for="invite_user_first_name">First Name</label>
                            <span class="help-block">The user will get the opportunity to confirm this.</span>
                        </div>
                        <div class="form-group form-md-line-input form-md-floating-label">
                            <input type="text" class="form-control input-sm" :class="{'edited': invitedUser.name.last_name && invitedUser.name.last_name.length}" id="invite_user_last_name" v-model="invitedUser.name.last_name">
                            <label for="invite_user_last_name">Last Name</label>
                            <span class="help-block">The user will get the opportunity to confirm this.</span>
                        </div>
                        <div class="form-group form-md-line-input form-md-floating-label" v-if="!locationId">
                        	<typeahead :data="roles" :label="'Role'" :emitter="'role'" @role="updateRole" :objectKey="'role_name'" @onFocus="roleFocus"></typeahead>
                        </div>
                        <div class="form-group form-md-line-input form-md-floating-label" v-else>
                        	<typeahead :data="locationRoles" :label="'Location Role'" :emitter="'locationRole'" @locationRole="updateLocationRole" :objectKey="'role_name'" @onFocus="locationRoleFocus"></typeahead>
                        </div>
					</div>
					<div class="form-actions noborder invite-user-actions">
						<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To Users Dashboard</button>
						<button type="submit" class="btn blue">Submit</button>
					</div>
				</form>
			</div>
		</div>
		<next-actions v-else :actionsList="inviteUserNextActions" @runFunction="runFunction"></next-actions>
	</div>
</template>
<script>
import $ from 'jquery'
import NextActions from '../../../modules/NextActions'
import GlobalFunctions from '../../../../global'
import Auth from '../../../../controllers/Auth'
import Role from '../../../../controllers/Roles'
import Typeahead from '../../../modules/Typeahead'

/**
 * Invite User module is a page for inviting new users to the system.
 * @class InviteUser
 * @extends Users
 * @prop {number} businessId - Used to pass the business id of the business the user is being invited to.
 * @prop {string} baseRoute - The route of the component that this is being called from.
 * @prop {string} baseRouteName - The name of the route of the component that this is being called from.
 * @since 0.0.5
 */

/**
 * Define the email pattern to check for valid emails.
 * @var {regex}
 * @memberof InviteUser
 * @version 0.0.5
 */
const emailPattern = /^.+@.+\..+$/

export default {
	name: 'invite-user',
	props: {
		businessId: {
			type: Number,
			required: true
		},
		locationId: {
			type: Number,
			required: false
		},
		baseRoute: {
			type: String,
			required: true
		},
		baseRouteName: {
			type: String,
			required: true
		}
	},
	data () {
		return {
			process: 'form',
			activePanel: false,
			errorMessage: '',
			invitedUser: {
				name: {
					first_name: '',
					last_name: ''
				},
				email: '',
				role: ''
			},
			roleFocused: false,
			roles: [],
			locationRoleFocused: false,
			locationRoles: [],
			inviteUserNextActions: [{title: 'Invite another user', icon_class: 'fa fa-user', click_event: 'resetProcess'}, {title: 'Return to User Dashboard', icon_class: 'fa fa-arrow-circle-left', click_event: 'closePanel'}]
		}
	},
	/**
	 * Run on created to determine if this should be the active panel or not.
	 * @function
	 * @return {undefined}
	 * @memberof InviteUser
	 * @version 0.0.8
	 */
	created () {
		if (this.$route.name === this.baseRouteName + ' - Invite User') {
			this.activePanel = true
		}
	},
	methods: {
		/**
		 * To clear the current error.
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.5
		 */
		clearError () {
			this.errorMessage = ''
		},
		/**
		 * To check if the email and role are valid before submitting to the backend.
		 * @function
		 * @returns {object} A promise that will validate the input form
		 * @memberof InviteUser
		 * @version 0.0.6
		 */
		validateUserData () {
			let _this = this
			return new Promise((resolve, reject) => {
				if (!_this.invitedUser.email.length) {
					reject('User email cannot be blank')
				} else if (!emailPattern.test(_this.invitedUser.email)) {
					reject('Please enter a valid email')
				} else if ((_this.locationId && !_this.invitedUser.location_role) || (!_this.locationId && !_this.invitedUser.role)) {
					reject('User role cannot be blank')
				}
				resolve('Hurray')
			})
		},
		/**
		 * To send the details of the user to be invited to the backend.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {object} A promise that will either return an error message or display the success screen
		 * @memberof InviteUser
		 * @version 0.0.8
		 */
		inviteUser (event) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this

			return _this.validateUserData()
			.then(response => {
				_this.clearError()
				Auth.inviteUser(_this.invitedUser, _this.businessId, _this.locationId).then(response => {
					_this.clearForm()
					_this.process = 'next_actions'
					disabledButton.complete()
				}).catch(_this.$root.errorWrapper(e => {
					if (e.responseJSON) {
						switch (e.responseJSON.declaration) {
						case 'email_failed':
							_this.errorMessage = 'We were unable to process the request. Please check the email and try again.'
							break

						case 'invalid_business':
							_this.errorMessage = 'You lack proper permissions for this invitation. Please contact the admin.'
							break

						case 'already_invited':
							_this.errorMessage = 'This user is already a member of your business.'
							break

						case 'business_not_found':
							_this.errorMessage = 'The business you are inviting the user to doesn’t exist. Please contact the admin.'
							break

						case 'too_many_accounts':
							_this.errorMessage = 'The user you are inviting has too many contacts. Try another email.'
							break

						default:
							// No default
						}
					}
					disabledButton.cancel()
				}))
			}).catch(reason => {
				// If validation fails then display the error message
				_this.errorMessage = reason
				disabledButton.cancel()
			})
		},
		/**
		 * To clear the object 'invitedUser'.
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.5
		 */
		clearForm () {
			this.invitedUser = {
				name: {
					first_name: '',
					last_name: ''
				},
				email: '',
				role: ''
			}
			$('.invite-user-form').find('input, select').removeClass('edited')
		},
		/**
		 * To run the function passed in.
		 * @function
		 * @param {string} functionName - The function that is to be run
		 * @param {array} params - The list of parameters being passed into the function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.5
		 */
		runFunction (functionName, params) {
			if (!params) {
				this[functionName]()
			} else {
				this[functionName](params[0])
			}
		},
		/**
		 * To reset the panel to display the form.
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.5
		 */
		resetProcess () {
			this.process = 'form'
		},
		/**
		 * To close the invite user panel by redirecting to the dashboard
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.8
		 */
		closePanel () {
			this.process = 'form'
			this.$router.push(this.baseRoute)
		},
		/**
		 * To update the role search field based on the value sent from the typeahead component
		 * @function
		 * @param {object} val - the selected role object.
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.6
		 */
		updateRole (val) {
			if (val) {
				this.invitedUser.role = val
			} else {
				this.invitedUser.role = {}
			}
		},
		/**
		 * To update the location role search field based on the value sent from the typeahead component
		 * @function
		 * @param {object} val - the selected location role object.
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.9
		 */
		updateLocationRole (val) {
			if (val) {
				this.invitedUser.location_role = val
			} else {
				this.invitedUser.location_role = {}
			}
		},
		/**
		 * To check if this is the first time the role field has been focused on in order to pull in all roles.
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.6
		 */
		roleFocus () {
			if (!this.roleFocused) {
				this.roleFocused = true
				this.getRoles()
			}
		},
		/**
		 * To check if this is the first time the location role field has been focused on in order to pull in all location roles.
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.9
		 */
		locationRoleFocus () {
			if (!this.locationRoleFocused) {
				this.locationRoleFocused = true
				this.getLocationRoles()
			}
		},
		/**
		 * To get a list of all roles for the company
		 * @function
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 * @memberof InviteUser
		 * @version 0.0.8
		 */
		getRoles () {
			let _this = this

			return Role.getRoles(this.businessId).then(response => {
				_this.roles = response.payload.roles
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'no_roles_found':
						// This should never happen but if it does then the user should just not have a list of roles to choose from
						break

					case 'business_not_found':
						_this.$swal({
							title: 'Error',
							html: 'There is an issue with your business account. Please contact your admin.',
							type: 'warning'
						})
						break

					case 'invalid_business':
						_this.$swal({
							title: 'Error',
							html: 'You do not have access to this business.',
							type: 'warning'
						})
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To get a list of all roles for the location
		 * @function
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 * @memberof InviteUser
		 * @version 0.0.9
		 */
		getLocationRoles () {
			let _this = this

			return Role.getLocationRoles(this.locationId).then(response => {
				_this.locationRoles = response.payload.roles
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To redirect to the approval permissions panel
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.8
		 */
		redirectToPermissions () {
			this.process = 'form'
			this.$router.push(this.baseRoute + '/approval_permissions')
		}
	},
	watch: {
		/**
		 * To `watch` the $route in order to determine if this panel should be showing or not
		 * @function
		 * @returns {undefined}
		 * @memberof InviteUser
		 * @version 0.0.8
		 */
		'$route' () {
			if (this.$route.name === this.baseRouteName + ' - Invite User') {
				this.activePanel = true
				this.process = 'form'
			} else {
				this.activePanel = false
			}
		}
	},
	components: {
		NextActions,
		Typeahead
	}
}
</script>



// WEBPACK FOOTER //
// InviteUser.vue?5413d1fc